import { Suspense, useCallback, useState, type ReactNode } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

import { useSession } from '@/features/auth/useSession';
import { layout, radius } from '@/theme';

import { LazyComposerDialog } from '@/app/lazy-routes';
import { PageTransition } from '@/components/common/PageTransition';

import { AppHeader } from './AppHeader';
import { BottomNav } from './BottomNav';
import { NavRail } from './NavRail';
import { VerifyEmailBanner } from './VerifyEmailBanner';

interface AppShellProps {
  /** Right-hand column. Absent on routes that do not have one. */
  sidebar?: ReactNode;
  /**
   * Lets the centre column use the sidebar's width too.
   *
   * Off by default: a settings form or a post at 970px has lines twice as long
   * as the feed's. The map is the exception — it is a picture, not prose, and
   * every pixel of width is more of the world.
   */
  wide?: boolean;
}

export interface ShellOutletContext {
  onCompose: () => void;
}

/**
 * The responsive frame (spec §9.3).
 *
 *   desktop (md+)  nav rail · feed (max 680) · sidebar
 *   tablet (sm–md) feed · sidebar, rail collapses into the bottom bar
 *   mobile (xs)    single column, bottom navigation
 *
 * The rail and the bottom bar are rendered together and shown by breakpoint
 * rather than swapped by a JS media query — no layout shift on first paint and
 * nothing to get wrong during hydration.
 */
export function AppShell({ sidebar, wide = false }: AppShellProps) {
  const { t } = useTranslation();
  const session = useSession();
  const [composerOpen, setComposerOpen] = useState(false);

  // Both the desktop rail and the mobile FAB open the same dialog, so it is
  // mounted once here rather than per entry point.
  const handleCompose = useCallback(() => setComposerOpen(true), []);

  return (
    <Box sx={{ minHeight: '100dvh', backgroundColor: 'background.default' }}>
      {/* First thing in the tab order: a keyboard reader should not have to
          walk the whole header and rail on every page to reach the content. */}
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'absolute',
          left: 8,
          top: -64,
          zIndex: (theme) => theme.zIndex.tooltip,
          px: 2,
          py: 1,
          borderRadius: `${radius.input}px`,
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          color: 'text.primary',
          textDecoration: 'none',
          '&:focus-visible': { top: 8 },
        }}
      >
        {t('common.skipToContent')}
      </Box>

      <AppHeader session={session} />

      <Container
        maxWidth={false}
        sx={{
          maxWidth: layout.pageMaxWidth,
          pt: 3,
          // Clear the fixed bottom bar and the compose FAB on small screens.
          // The bar, the compose button above it, and whatever the device
          // reserves at the bottom — the last of which was missing, so on a
          // phone with a home indicator the final row of any page sat under
          // the bar.
          pb: {
            xs: `calc(${layout.bottomNavHeight}px + 32px + env(safe-area-inset-bottom))`,
            md: 6,
          },
        }}
      >
        {/*
          A grid, not a centred row.
          
          A centred row re-balances itself whenever a page has no sidebar, which
          made the nav rail slide sideways on the way to the map. Fixed track
          widths keep every column where it was: the rail under the logo, the
          sidebar under the account menu, and the feed filling what is left.
        */}
        <Box
          sx={{
            display: 'grid',
            columnGap: 3,
            alignItems: 'start',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: `${layout.navRailWidth}px minmax(0, 1fr)`,
              lg: `${layout.navRailWidth}px minmax(0, 1fr) ${layout.sidebarWidth}px`,
            },
          }}
        >
          <NavRail
            isSignedIn={session.isSignedIn}
            {...(session.user ? { role: session.user.role } : {})}
            businessSlug={session.user?.businessSlug ?? null}
            capabilities={session.user?.businessCapabilities ?? []}
            onCompose={handleCompose}
          />

          <Box
            component="main"
            id="main-content"
            sx={{
              width: '100%',
              minWidth: 0,
              ...(wide ? {} : { maxWidth: layout.contentMaxWidth }),
              // With no sidebar the centre column is free to use that track.
              ...(sidebar ? {} : { gridColumn: { lg: '2 / -1' } }),
            }}
          >
            {session.needsEmailVerification && session.user && (
              <VerifyEmailBanner email={session.user.email} />
            )}
            <PageTransition>
              <Outlet context={{ onCompose: handleCompose } satisfies ShellOutletContext} />
            </PageTransition>
          </Box>

          {sidebar && (
            <Box
              component="aside"
              aria-label={t('shell.sidebar')}
              sx={{
                display: { xs: 'none', lg: 'block' },
                position: 'sticky',
                top: layout.appBarHeight + 16,
                alignSelf: 'flex-start',
                // Sticky content taller than the viewport is content nobody can
                // reach: the column stops scrolling with the page but has
                // nowhere else to go. Same treatment as the nav rail.
                maxHeight: `calc(100dvh - ${layout.appBarHeight + 32}px)`,
                overflowY: 'auto',
              }}
            >
              {sidebar}
            </Box>
          )}
        </Box>
      </Container>

      <BottomNav
        isSignedIn={session.isSignedIn}
        {...(session.user ? { role: session.user.role } : {})}
        businessSlug={session.user?.businessSlug ?? null}
        capabilities={session.user?.businessCapabilities ?? []}
        onCompose={handleCompose}
      />

      {/* Mounted only once someone decides to write (spec §11): the composer
          pulls in the form stack, the upload pipeline and the bad-post picker,
          none of which a reader needs to read. */}
      {session.isSignedIn && composerOpen && (
        <Suspense fallback={null}>
          <LazyComposerDialog open onClose={() => setComposerOpen(false)} />
        </Suspense>
      )}
    </Box>
  );
}
